import type { TFunction } from "i18next";
import { PickupDeliveryConsequences } from "./pickup-delivery-consequences";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CreditCard,
  FileText,
  Info,
  MapPin,
  Wallet,
} from "lucide-react";
import {
  SheetSection,
  SelectableCard,
  SlotCapacityLabel,
  RadioDot,
  CheckBox,
} from "./sheet-primitives";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Spinner,
  Input,
  Textarea,
  Select,
  SelectItem,
} from "@heroui/react";
import { useToast } from "@/hooks/use-toast";
import {
  PickupEligibilityNotice,
  PickupUnavailableShipments,
} from "./pickup-eligibility-notice";
import { usePickupSelectionRecovery } from "./use-pickup-selection-recovery";
import {
  useMyPaymentTerms,
  usePaymentMethods,
} from "@/api/use-payment-methods-api";
import { useCreditBalance } from "@/api/use-payment-options-api";
import {
  formatDateInTz,
  formatSlotRangeInTz,
  todayIsoInTz,
} from "@/lib/dispatch-time";
import {
  isPickupBookingError,
  useCreatePickupBooking,
  useEligiblePickupShipments,
  usePickupOptionsForAddress,
  usePreviewPickupBooking,
  type EligibleShipment,
  type PickupOptions,
  type PickupPreview,
} from "@/api/use-schedule-pickup";
import { useTranslation } from "react-i18next";
import { useAttachablePickupBookings } from "@/api/use-attachable-pickup-bookings";
import { useMyPickupBooking } from "@/api/use-my-pickup-bookings";
import { AttachToBookingModal } from "./attach-to-booking-modal";
import { ContactForm } from "@/features/address-book/components/ContactForm";
import {
  useCreateContact,
  useContact,
  useCustomerContacts,
} from "@/features/address-book/api";
import type {
  CreateContactInput,
  CustomerContact,
} from "@/features/address-book/types";
import { pickupSlotsForSelectedDate } from "./pickup-date-availability";

/** Address-first pickup draft; location and label origins are independent. */

interface Props {
  open: boolean;
  onClose: () => void;
  initialShipmentIds?: string[];
}

type Step = "where" | "what" | "when" | "confirm";
const STEPS: Step[] = ["where", "what", "when", "confirm"];
type PickupPaymentChoice = "SAVED_CARD" | "CREDIT" | "INVOICE";

const NOTES_MAX = 500;

function generateIdempotencyKey(): string {
  return crypto.randomUUID();
}

function formatCurrency(cents: number, currency = "CAD"): string {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency,
  }).format(cents / 100);
}

type SenderAddress = NonNullable<EligibleShipment["senderAddress"]> & {
  country?: string;
};

const UNGROUPED_KEY = "__unbookable__";

interface ShipmentGroup {
  key: string; // senderAddressId or UNGROUPED_KEY
  addressId: string | null;
  address: SenderAddress | null;
  shipments: EligibleShipment[];
}

function formatAddressLine(
  addr: SenderAddress | null,
  missingLabel: string
): string {
  if (!addr) return missingLabel;
  const cityProv = [addr.city, addr.province].filter(Boolean).join(", ");
  return [addr.line1, addr.line2, cityProv, addr.postalCode, addr.country]
    .filter((v) => v && v.trim().length > 0)
    .join(" · ");
}

function groupShipments(shipments: EligibleShipment[]): ShipmentGroup[] {
  const byKey = new Map<string, ShipmentGroup>();
  for (const s of shipments) {
    const key = s.senderAddressId ?? UNGROUPED_KEY;
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        addressId: s.senderAddressId,
        address: s.senderAddress,
        shipments: [],
      };
      byKey.set(key, group);
    }
    group.shipments.push(s);
  }
  // Stable order: bookable groups first (alpha by address line1), unbookable last.
  const groups = Array.from(byKey.values());
  groups.sort((a, b) => {
    if (a.key === UNGROUPED_KEY) return 1;
    if (b.key === UNGROUPED_KEY) return -1;
    const al = a.address?.line1 ?? "";
    const bl = b.address?.line1 ?? "";
    return al.localeCompare(bl);
  });
  return groups;
}

function isPickupFulfillmentTimingError(error: unknown) {
  if (
    !isPickupBookingError(error) ||
    error.response?.data.error !== "PICKUP_FULFILLMENT_UNAVAILABLE"
  )
    return false;
  const reason = error.response.data.details?.reason;
  return (
    reason === "NO_DELIVERY_OPPORTUNITY" || reason === "MISSED_INDUCTION_DAY"
  );
}

function isPickupFulfillmentSetupError(error: unknown) {
  return (
    isPickupBookingError(error) &&
    error.response?.data.error === "PICKUP_FULFILLMENT_UNAVAILABLE" &&
    !isPickupFulfillmentTimingError(error)
  );
}

function pickupFulfillmentMessage(error: unknown, t: TFunction<"app">) {
  if (
    !isPickupBookingError(error) ||
    error.response?.data.error !== "PICKUP_FULFILLMENT_UNAVAILABLE"
  )
    return undefined;
  if (isPickupFulfillmentTimingError(error))
    return t("pickup.recovery.timeUnavailable");
  const shipmentNumber = error.response.data.details?.shipmentNumber;
  return typeof shipmentNumber === "number" &&
    Number.isSafeInteger(shipmentNumber) &&
    shipmentNumber > 0
    ? t("pickup.recovery.pickupUnavailableForShipment", { shipmentNumber })
    : t("pickup.recovery.pickupUnavailableSelection");
}

export function SchedulePickupSheet({
  open,
  onClose,
  initialShipmentIds,
}: Props) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const [step, setStep] = useState<Step>("where");
  const stepLabels = t("pickup.steps.labels", {
    returnObjects: true,
  }) as string[];
  const stepRailLabels = t("pickup.steps.rails", {
    returnObjects: true,
  }) as string[];
  const stepHeadlines = t("pickup.steps.headlines", {
    returnObjects: true,
  }) as string[];

  // Wizard state
  const [selectedShipmentIds, setSelectedShipmentIds] = useState<Set<string>>(
    new Set(initialShipmentIds)
  );
  const [date, setDate] = useState<string>("");
  const [pickupServiceId, setPickupServiceId] = useState<string>("");
  const [slotId, setSlotId] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [paymentChoice, setPaymentChoice] = useState<PickupPaymentChoice | "">(
    ""
  );
  const [paymentMethodId, setPaymentMethodId] = useState<string>("");
  const [quote, setQuote] = useState<{
    key: string;
    data: PickupPreview;
  } | null>(null);
  const [recoveryMessage, setRecoveryMessage] = useState<string | null>(null);
  const submission = useRef<{ identity: string; key: string } | null>(null);
  const previewVersion = useRef(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [existingBookingId, setExistingBookingId] = useState<string | null>(
    null
  );
  const [reviewExisting, setReviewExisting] = useState(false);
  const existingBooking = useMyPickupBooking(existingBookingId);
  // Note: state resets via parent's `key` prop on open/close transitions.

  // Step 1: eligible shipments (no args now)
  const { data: eligibleShipments, isLoading: loadingShipments } =
    useEligiblePickupShipments();
  const recovery = usePickupSelectionRecovery(
    eligibleShipments,
    selectedShipmentIds,
    setSelectedShipmentIds
  );

  // Group by senderAddressId. Memo on the data array — Map identity stable.
  const groups = useMemo(
    () => groupShipments(eligibleShipments ?? []),
    [eligibleShipments]
  );

  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [addingNew, setAddingNew] = useState(false);
  const saveContact = useCreateContact();
  const contacts = useCustomerContacts({ search, limit: 10, offset });
  const selectedContactQuery = useContact(selectedEntryId ?? "");
  const selectedContact = selectedEntryId
    ? selectedContactQuery.data
    : undefined;
  const location = selectedContact
    ? {
        address: {
          line1: selectedContact.addressLine1,
          line2: selectedContact.addressLine2 ?? null,
          city: selectedContact.addressCity,
          province: selectedContact.addressProvince,
          country: selectedContact.addressCountry,
          postalCode: selectedContact.addressPostalCode ?? null,
        },
      }
    : undefined;
  const pickupContact = selectedContact
    ? {
        contactName: selectedContact.contactName,
        contactCompany: selectedContact.contactCompany ?? null,
        contactPhone: selectedContact.contactPhone ?? null,
        contactEmail: selectedContact.contactEmail ?? null,
        instructions: selectedContact.pickupInstructions ?? null,
        isResidential: selectedContact.propertyResidential,
      }
    : null;
  const chooseLocation = (entryId: string) => {
    setSelectedEntryId(entryId);
    setSubmitError(null);
    setExistingBookingId(null);
  };
  const saveNewContact = async (input: CreateContactInput) => {
    try {
      const saved = await saveContact.mutateAsync(input);
      chooseLocation(saved.id);
      setAddingNew(false);
    } catch {
      toast({ title: t("pickup.where.saveError"), variant: "destructive" });
    }
  };

  // Selected shipments (full objects) — referenced by Step 2 + Step 3.
  const selectedShipments = useMemo<EligibleShipment[]>(() => {
    if (!eligibleShipments) return [];
    return eligibleShipments.filter((s) => selectedShipmentIds.has(s.id));
  }, [eligibleShipments, selectedShipmentIds]);

  const compatibleVisits = useAttachablePickupBookings({
    addressBookEntryId: selectedEntryId ?? undefined,
    shipmentIds: Array.from(selectedShipmentIds),
  });
  const matchingVisits = selectedEntryId
    ? (compatibleVisits.data?.data ?? [])
    : [];

  // Confirmed options require both the physical location and selected parcels.
  const readyEntryId = selectedContact ? selectedEntryId : null;
  const {
    data: pickupOptions,
    isLoading: loadingOptions,
    error: optionsError,
    refetch: refetchOptions,
  } = usePickupOptionsForAddress(
    readyEntryId,
    Array.from(selectedShipmentIds),
    date || null
  );
  const selectedPickupService = pickupOptions?.pickupServices.find(
    (service) => service.id === pickupServiceId
  );

  const draftKey = JSON.stringify([
    selectedEntryId,
    readyEntryId,
    pickupContact,
    selectedShipments,
    pickupServiceId,
    selectedPickupService?.dispatch,
    slotId,
    date,
    recovery.unavailable,
  ]);
  const currentDraftKey = useRef(draftKey);
  currentDraftKey.current = draftKey;
  const preview = quote?.key === draftKey ? quote.data : null;
  const reviewContact = preview?.addressBookContact ?? pickupContact;
  const reviewAddress = preview?.pickupAddress ?? location?.address;
  const setPreview = useCallback(
    (data: PickupPreview | null) =>
      setQuote(data ? { key: draftKey, data } : null),
    [draftKey]
  );
  useEffect(() => {
    previewVersion.current += 1;
    setQuote(null);
    setExistingBookingId(null);
  }, [draftKey]);

  // Step 3: payment methods (lazy)
  const { data: paymentMethodsData } = usePaymentMethods();
  const paymentMethods = paymentMethodsData?.paymentMethods ?? [];
  const { data: creditBalance } = useCreditBalance();
  const { data: paymentTermsData } = useMyPaymentTerms();

  const previewMutation = usePreviewPickupBooking();
  const createMutation = useCreatePickupBooking();

  // Initialise `date` to tomorrow in the dispatch's tz once we know it.
  // Render-phase pattern: only run when the upstream changes.
  const [prevDispatchTz, setPrevDispatchTz] = useState<string | null>(null);
  const dispatchTimeZone = selectedPickupService?.dispatch.timeZone ?? null;
  if (dispatchTimeZone !== prevDispatchTz) {
    setPrevDispatchTz(dispatchTimeZone);
    if (dispatchTimeZone && date === "") {
      // Default to today-in-tz; server will return slot capacity for that day.
      setDate(todayIsoInTz(dispatchTimeZone));
    }
  }

  // A location change clears its service; select the sole compatible option again.
  useEffect(() => {
    if (pickupOptions?.pickupServices.length === 1 && pickupServiceId === "") {
      setPickupServiceId(pickupOptions.pickupServices[0].id);
    }
  }, [pickupOptions, pickupServiceId]);

  // Default payment method (first saved card). Same render-phase pattern.
  const [prevPaymentMethodsLen, setPrevPaymentMethodsLen] = useState(0);
  if (paymentMethods.length !== prevPaymentMethodsLen) {
    setPrevPaymentMethodsLen(paymentMethods.length);
    if (paymentMethodId === "" && paymentMethods.length > 0) {
      const def =
        paymentMethods.find((p) => p.isDefault)?.id ?? paymentMethods[0]?.id;
      if (def) {
        setPaymentMethodId(def);
        if (paymentChoice === "") setPaymentChoice("SAVED_CARD");
      }
    }
  }

  // Filter slots client-side by booking cutoff (mirrors server guard).
  const bookableSlots = useMemo(() => {
    if (!pickupOptions || !dispatchTimeZone || !date) return [];
    return pickupSlotsForSelectedDate(
      pickupOptions.timeSlots.filter(
        (slot) => slot.pickupServiceId === pickupServiceId
      ),
      date,
      dispatchTimeZone
    );
  }, [pickupOptions, dispatchTimeZone, date, pickupServiceId]);

  useEffect(() => {
    if (loadingOptions || optionsError || !pickupOptions) return;
    const incompatibleService =
      pickupServiceId &&
      !pickupOptions.pickupServices.some(
        (service) => service.id === pickupServiceId
      );
    const unavailableSlot =
      slotId &&
      !bookableSlots.some((slot) => slot.id === slotId && slot.remaining !== 0);
    if (incompatibleService) setPickupServiceId("");
    if (incompatibleService || unavailableSlot) {
      setSlotId("");
      if (step === "confirm") {
        setStep("when");
        setRecoveryMessage(t("pickup.recovery.availabilityChanged"));
      }
    }
  }, [
    loadingOptions,
    optionsError,
    pickupOptions,
    pickupServiceId,
    slotId,
    bookableSlots,
    step,
    t,
  ]);

  useEffect(() => {
    if (step !== "confirm" || !optionsError) return;
    setStep("when");
    setRecoveryMessage(
      isPickupFulfillmentSetupError(optionsError)
        ? null
        : (pickupFulfillmentMessage(optionsError, t) ??
            (isPickupBookingError(optionsError) &&
            optionsError.response?.data.message
              ? optionsError.response.data.message
              : t("pickup.where.optionsError")))
    );
  }, [step, optionsError, t]);

  const recoverError = (error: unknown) => {
    const code = isPickupBookingError(error)
      ? error.response?.data.error
      : undefined;
    const message =
      pickupFulfillmentMessage(error, t) ??
      (isPickupBookingError(error) && error.response?.data.message
        ? error.response.data.message
        : t("pickup.wizard.scheduleFailed"));
    if (code === "PICKUP_FULFILLMENT_UNAVAILABLE") {
      setPreview(null);
      setRecoveryMessage(message);
      if (isPickupFulfillmentTimingError(error)) {
        setSlotId("");
        setStep("when");
        void refetchOptions();
      } else {
        setStep("what");
      }
      return true;
    }
    if (code === "PREVIEW_CHANGED") {
      setPreview(null);
      setStep("when");
      setRecoveryMessage(message);
      void selectedContactQuery.refetch();
      return true;
    }
    if (
      code === "SLOT_CLOSED" ||
      code === "SLOT_FULL" ||
      code === "PAST_DATE"
    ) {
      setPreview(null);
      setSlotId("");
      setStep("when");
      setRecoveryMessage(message);
      void refetchOptions();
      return true;
    }
    if (
      code === "ADDRESS_NOT_OWNED" ||
      code === "ADDRESS_NOT_FOUND" ||
      code === "SERVICE_AREA_NOT_FOUND"
    ) {
      setPreview(null);
      setStep("where");
      setRecoveryMessage(message);
      return true;
    }
    if (
      code === "DISPATCH_SPLIT_NOT_SUPPORTED" ||
      code === "DISPATCH_UNSERVABLE" ||
      code === "PACKAGE_WEIGHT_UNPRICED" ||
      code === "PICKUP_LEG_UNPRICED"
    ) {
      setPreview(null);
      setStep("what");
      setRecoveryMessage(message);
      return true;
    }
    return false;
  };

  const pickupTotalCents = preview ? preview.totalCents + preview.taxCents : 0;
  const canUseCredit =
    !!creditBalance &&
    !creditBalance.frozen &&
    pickupTotalCents > 0 &&
    creditBalance.balanceCents >= pickupTotalCents;
  const selectedOrdersUseNetTerms =
    selectedShipments.length > 0 &&
    selectedShipments.every(
      (s) =>
        s.order.paymentMethodType === "INVOICE" &&
        !!s.order.paymentTermsId &&
        !!s.order.paymentTermsRef &&
        s.order.invoicePending
    );
  const selectedTermNames = Array.from(
    new Set(
      selectedShipments
        .map((s) => s.order.paymentTermsRef?.name)
        .filter((name): name is string => !!name)
    )
  );
  const canUseNetTerms =
    !!paymentTermsData &&
    selectedOrdersUseNetTerms &&
    pickupTotalCents > 0 &&
    !paymentTermsData.hasUnpaidInvoices &&
    paymentTermsData.availableCredit >= pickupTotalCents;
  const paymentReady =
    paymentChoice === "SAVED_CARD"
      ? paymentMethods.some((method) => method.id === paymentMethodId)
      : paymentChoice === "CREDIT"
        ? canUseCredit
        : paymentChoice === "INVOICE"
          ? canUseNetTerms
          : false;

  // Wizard navigation gates ------------------------------------------------

  const contactValid = !!selectedContact && !selectedContactQuery.isError;
  const stepIndex = STEPS.indexOf(step);
  const pickupSetupUnavailable =
    step === "when" && isPickupFulfillmentSetupError(optionsError);
  const canAdvance = useMemo(() => {
    if (recovery.unavailable.length) return false;
    switch (step) {
      case "where":
        return !!readyEntryId && contactValid;
      case "what":
        return selectedShipmentIds.size > 0 && !!readyEntryId;
      case "when": {
        if (!pickupOptions || optionsError) return false;
        if (!pickupOptions.serviceAreaCovered) return false;
        if (pickupOptions.pickupServices.length === 0) return false;
        if (!selectedPickupService?.price || !slotId || !date) return false;
        // Block advancing on a slot that filled after it was selected — the
        // server would reject with SLOT_FULL on submit anyway.
        const chosen = bookableSlots.find((s) => s.id === slotId);
        if (!chosen || chosen.remaining === 0) return false;
        return true;
      }
      case "confirm":
        return (
          !!preview &&
          !!selectedPickupService?.price &&
          paymentReady &&
          !existingBookingId &&
          !optionsError &&
          !!pickupOptions?.pickupServices.some(
            (service) => service.id === pickupServiceId
          ) &&
          bookableSlots.some(
            (slot) => slot.id === slotId && slot.remaining !== 0
          )
        );
    }
  }, [
    step,
    contactValid,
    selectedShipmentIds,
    readyEntryId,
    pickupOptions,
    optionsError,
    pickupServiceId,
    selectedPickupService,
    slotId,
    date,
    preview,
    bookableSlots,
    paymentReady,
    existingBookingId,
    recovery.unavailable.length,
  ]);

  // Step transition: when leaving "when", fetch the preview for "confirm".
  const goNext = async () => {
    if (recovery.unavailable.length) return;
    if (step === "when") {
      if (!readyEntryId || !selectedEntryId || !pickupContact) return;
      const version = ++previewVersion.current;
      const key = draftKey;
      try {
        const result = await previewMutation.mutateAsync({
          addressBookEntryId: selectedEntryId,
          pickupServiceId,
          shipmentIds: Array.from(selectedShipmentIds),
          // Phase 12 (#232): pass date + slot so server can position the new
          // shipments against any existing booking and surface bundle savings.
          scheduledDate: date,
          slotId,
        });
        if (
          version !== previewVersion.current ||
          key !== currentDraftKey.current
        )
          return;
        setPreview(result);
        setRecoveryMessage(null);
        setSubmitError(null);
        setStep("confirm");
      } catch (err) {
        if (
          version !== previewVersion.current ||
          key !== currentDraftKey.current
        )
          return;
        if (recovery.reportError(err)) {
          setPreview(null);
          return;
        }
        if (recoverError(err)) return;
        const msg =
          isPickupBookingError(err) &&
          err.response?.data.error === "PACKAGE_WEIGHT_UNPRICED"
            ? t("createShipment.pickupPackageWeightUnpriced")
            : isPickupBookingError(err) && err.response?.data.message
              ? err.response.data.message
              : t("pickup.wizard.pricingFailedBody");
        setRecoveryMessage(msg);
        toast({
          title: t("pickup.wizard.pricingFailedTitle"),
          description: msg,
          variant: "destructive",
        });
      }
      return;
    }
    setStep(STEPS[stepIndex + 1]);
  };

  const goBack = () => {
    if (stepIndex === 0) {
      onClose();
      return;
    }
    setStep(STEPS[stepIndex - 1]);
  };

  // Submit -----------------------------------------------------------------

  const handleSubmit = async () => {
    if (recovery.unavailable.length) return;
    if (
      !readyEntryId ||
      !selectedEntryId ||
      !pickupContact ||
      !preview ||
      !paymentReady ||
      paymentChoice === ""
    )
      return;
    setSubmitError(null);
    const identity = JSON.stringify([
      draftKey,
      preview,
      paymentChoice,
      paymentMethodId,
      notes,
    ]);
    if (submission.current?.identity !== identity)
      submission.current = { identity, key: generateIdempotencyKey() };
    try {
      const result = await createMutation.mutateAsync({
        addressBookEntryId: selectedEntryId,
        expectedQuote: preview,
        pickupServiceId,
        shipmentIds: Array.from(selectedShipmentIds),
        scheduledDate: date,
        slotId,
        paymentMethodType: paymentChoice,
        paymentMethodId:
          paymentChoice === "SAVED_CARD" ? paymentMethodId : null,
        notes: notes.trim() === "" ? null : notes.trim(),
        idempotencyKey: submission.current.key,
      });
      const dateLabel = dispatchTimeZone
        ? formatDateInTz(date, dispatchTimeZone, "EEE, MMM d", locale)
        : date;
      const slotLabel =
        pickupOptions?.timeSlots.find((s) => s.id === slotId)?.name ?? "";
      toast({
        title: result.fromIdempotencyCache
          ? t("pickup.wizard.alreadyScheduled")
          : t("pickup.wizard.scheduled"),
        description: `${dateLabel} ${slotLabel}`.trim(),
      });
      onClose();
    } catch (err) {
      // Only a server-confirmed failed receipt permits a new provider attempt.
      // Keep the existing key for lost responses and other uncertain outcomes.
      if (
        isPickupBookingError(err) &&
        err.response?.data.error === "PAYMENT_FAILED" &&
        err.response.data.details?.paymentOutcome === "failed"
      ) {
        submission.current = null;
        setPreview(null);
        setStep("when");
        setRecoveryMessage(
          err.response.data.message ?? t("pickup.wizard.scheduleFailed")
        );
        return;
      }

      if (
        isPickupBookingError(err) &&
        err.response?.data.error === "BOOKING_EXISTS"
      ) {
        const bookingId = err.response.data.details?.bookingId;
        if (typeof bookingId === "string") setExistingBookingId(bookingId);
      }
      if (recovery.reportError(err)) {
        setPreview(null);
        return;
      }
      if (recoverError(err)) return;
      const msg =
        isPickupBookingError(err) && err.response?.data.message
          ? err.response.data.message
          : t("pickup.wizard.scheduleFailed");
      setSubmitError(msg);
    }
  };

  // Render -----------------------------------------------------------------

  const selectedSlot =
    pickupOptions?.timeSlots.find((s) => s.id === slotId) ?? null;

  return (
    <>
      <Drawer
        isOpen={open && !(reviewExisting && existingBooking.data) && !addingNew}
        onOpenChange={(v: boolean) => !v && onClose()}
        size="full"
        classNames={{ base: "!max-w-full sm:!max-w-[440px]" }}
      >
        <DrawerContent>
          <DrawerHeader className="flex-col items-start gap-2">
            <p className="text-tiny font-semibold uppercase tracking-wider text-default-500">
              {t("pickup.title")} · {stepLabels[stepIndex].split("·")[0].trim()}
            </p>
            <h2 className="text-2xl md:text-[26px] font-bold tracking-tight leading-tight">
              {pickupSetupUnavailable
                ? t("pickup.recovery.pickupUnavailable")
                : stepHeadlines[stepIndex]}
            </h2>
          </DrawerHeader>
          <div className="flex gap-2 px-4 sm:px-6 pb-4 border-b border-divider">
            {STEPS.map((s, i) => {
              const done = i < stepIndex;
              const active = i === stepIndex;
              return (
                <div key={s} className="flex-1 flex flex-col gap-1.5">
                  <div
                    className={`h-[3px] rounded-full ${
                      done || active ? "bg-primary" : "bg-default-200"
                    }`}
                  />
                  <div
                    className={`text-[11px] tracking-wide ${
                      active
                        ? "font-semibold text-default-900"
                        : done
                          ? "text-default-900"
                          : "text-default-500"
                    }`}
                  >
                    {stepRailLabels[i]}
                  </div>
                </div>
              );
            })}
          </div>
          <DrawerBody className="p-0">
            {recoveryMessage && (
              <SheetSection dense>
                <p role="alert" className="text-sm text-danger">
                  {recoveryMessage}
                </p>
              </SheetSection>
            )}
            <PickupEligibilityNotice
              shipments={recovery.unavailable}
              disabled={createMutation.isPending}
              onRemove={() => {
                previewVersion.current += 1;
                const remaining = recovery.removeUnavailable();
                setPreview(null);
                setSubmitError(null);
                setStep(remaining.size ? "when" : "what");
              }}
            />
            {step === "where" && (
              <PickupScheduleWhereStep
                contacts={contacts.data?.contacts ?? []}
                loading={contacts.isLoading || selectedContactQuery.isFetching}
                error={contacts.error ?? selectedContactQuery.error}
                selectedEntry={selectedContact}
                selectedEntryId={selectedEntryId}
                onSelect={chooseLocation}
                onRetry={() => {
                  if (contacts.error) void contacts.refetch();
                  if (selectedContactQuery.error)
                    void selectedContactQuery.refetch();
                }}
                search={search}
                onSearchChange={(value) => {
                  setSearch(value);
                  setOffset(0);
                }}
                total={contacts.data?.total ?? 0}
                offset={offset}
                onPageChange={setOffset}
                onNew={() => setAddingNew(true)}
              />
            )}
            {step !== "where" && reviewAddress && (
              <SheetSection dense title={t("pickup.where.selected")}>
                <p className="text-sm">
                  {formatAddressLine(reviewAddress, "")}
                </p>
                {step === "confirm" && reviewContact && (
                  <div className="text-xs text-default-500 mt-2 space-y-1">
                    <p>{reviewContact.contactName}</p>
                    <p>
                      {[
                        reviewContact.contactCompany,
                        reviewContact.contactPhone,
                        reviewContact.contactEmail,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    {reviewContact.instructions && (
                      <p>{reviewContact.instructions}</p>
                    )}
                    <p>
                      {t(
                        reviewContact.isResidential === null
                          ? "pickup.where.propertyUnknown"
                          : reviewContact.isResidential
                            ? "pickup.where.residential"
                            : "pickup.where.commercial"
                      )}
                    </p>
                    {notes && <p>{notes}</p>}
                  </div>
                )}
              </SheetSection>
            )}
            {step === "what" && (
              <PickupScheduleWhatStep
                groups={groups}
                loading={loadingShipments}
                selectedShipmentIds={selectedShipmentIds}
                setSelectedShipmentIds={setSelectedShipmentIds}
                notes={notes}
                setNotes={setNotes}
              />
            )}
            {step === "when" && matchingVisits.length > 0 && (
              <SheetSection
                title={t("pickup.recovery.existingVisits")}
                hint={t("pickup.where.existingHint")}
              >
                <div className="space-y-2">
                  {matchingVisits.map((visit) => (
                    <Button
                      key={visit.id}
                      className="h-auto whitespace-normal justify-start py-3"
                      fullWidth
                      variant="bordered"
                      isLoading={
                        reviewExisting &&
                        existingBooking.isLoading &&
                        existingBookingId === visit.id
                      }
                      onPress={() => {
                        setExistingBookingId(visit.id);
                        setReviewExisting(true);
                      }}
                    >
                      {t("pickup.recovery.addExisting", {
                        date: visit.scheduledDate.slice(0, 10),
                        time: formatSlotRangeInTz(
                          visit.slot,
                          visit.dispatchCenter.timeZone,
                          visit.scheduledDate.slice(0, 10),
                          locale
                        ),
                        service: visit.pickupService.name,
                      })}
                    </Button>
                  ))}
                </div>
                {existingBooking.error && (
                  <p role="alert" className="text-sm text-danger mt-2">
                    {t("pickup.where.loadError")}
                  </p>
                )}
              </SheetSection>
            )}
            {step === "when" && (
              <PickupScheduleWhenStep
                address={reviewAddress ?? null}
                pickupOptions={pickupOptions}
                error={optionsError}
                loading={loadingOptions}
                date={date}
                setDate={setDate}
                pickupServiceId={pickupServiceId}
                setPickupServiceId={setPickupServiceId}
                slotId={slotId}
                setSlotId={setSlotId}
                bookableSlots={bookableSlots}
                dispatchTimeZone={dispatchTimeZone}
                onBack={goBack}
                onChangeLocation={() => setStep("where")}
                onRetry={() => {
                  void refetchOptions();
                }}
              />
            )}
            {step === "confirm" && existingBookingId && (
              <SheetSection title={t("pickup.where.existingTitle")}>
                <p className="text-xs text-default-500 mb-3">
                  {t("pickup.where.existingHint")}
                </p>
                {existingBooking.error && (
                  <p role="alert" className="text-danger text-sm">
                    {t("pickup.where.loadError")}
                  </p>
                )}
                <Button
                  variant="bordered"
                  isLoading={existingBooking.isLoading}
                  isDisabled={!existingBooking.data}
                  onPress={() => setReviewExisting(true)}
                >
                  {t("pickup.where.reviewExisting")}
                </Button>
              </SheetSection>
            )}
            {step === "confirm" && (
              <PickupDeliveryConsequences
                consequences={preview?.deliveryConsequences}
                shipments={selectedShipments}
              />
            )}
            {step === "confirm" && (
              <StepConfirm
                address={reviewAddress ?? null}
                date={date}
                slot={selectedSlot}
                dispatchTimeZone={preview?.dispatch.timeZone ?? null}
                preview={preview}
                paymentMethods={paymentMethods}
                paymentChoice={paymentChoice}
                setPaymentChoice={setPaymentChoice}
                paymentMethodId={paymentMethodId}
                setPaymentMethodId={setPaymentMethodId}
                creditBalanceCents={creditBalance?.balanceCents ?? 0}
                creditFrozen={creditBalance?.frozen ?? false}
                canUseCredit={canUseCredit}
                canUseNetTerms={canUseNetTerms}
                netTermsLabel={
                  selectedTermNames.length > 0
                    ? selectedTermNames.join(", ")
                    : null
                }
                netTermsUnavailableReason={
                  !selectedOrdersUseNetTerms
                    ? t("pickup.confirm.netTermsRequiresSource")
                    : paymentTermsData?.hasUnpaidInvoices
                      ? t("pickup.confirm.netTermsBlockedInvoices")
                      : paymentTermsData &&
                          paymentTermsData.availableCredit < pickupTotalCents
                        ? t("pickup.confirm.netTermsInsufficientCredit")
                        : null
                }
                submitError={submitError}
              />
            )}
          </DrawerBody>
          {!pickupSetupUnavailable && (
            <DrawerFooter className="border-t border-divider bg-background flex items-center justify-between gap-3">
              <Button
                variant="light"
                onPress={goBack}
                isDisabled={createMutation.isPending}
                startContent={<ArrowLeft className="h-4 w-4" />}
              >
                {stepIndex === 0 ? t("actions.cancel") : t("actions.back")}
              </Button>
              {step !== "confirm" ? (
                <Button
                  color="primary"
                  onPress={goNext}
                  isDisabled={!canAdvance || previewMutation.isPending}
                  isLoading={previewMutation.isPending}
                  endContent={
                    !previewMutation.isPending ? (
                      <ArrowRight className="h-4 w-4" />
                    ) : null
                  }
                >
                  {t("actions.continue")}
                </Button>
              ) : (
                <Button
                  color="primary"
                  onPress={handleSubmit}
                  isDisabled={!canAdvance || createMutation.isPending}
                  isLoading={createMutation.isPending}
                >
                  {t("pickup.wizard.schedulePickup")}
                </Button>
              )}
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
      {addingNew && (
        <ContactForm
          open
          onClose={() => setAddingNew(false)}
          onSubmit={saveNewContact}
          isLoading={saveContact.isPending}
        />
      )}
      {reviewExisting && existingBooking.data && (
        <AttachToBookingModal
          open
          onClose={() => setReviewExisting(false)}
          onSuccess={onClose}
          booking={existingBooking.data}
          preselectedShipmentIds={Array.from(selectedShipmentIds)}
          onScheduleNew={(ids) => {
            setReviewExisting(false);
            setExistingBookingId(null);
            setSelectedShipmentIds(new Set(ids));
            setStep("where");
            setSlotId("");
            setPreview(null);
          }}
        />
      )}
    </>
  );
}

// ── Sub-steps ─────────────────────────────────────────────────────────────

export function PickupScheduleWhereStep({
  contacts,
  loading,
  error,
  selectedEntry,
  selectedEntryId,
  onSelect,
  onRetry,
  search,
  onSearchChange,
  total,
  offset,
  onPageChange,
  onNew,
}: {
  contacts: CustomerContact[];
  loading: boolean;
  error: unknown;
  selectedEntry?: CustomerContact;
  selectedEntryId: string | null;
  onSelect: (id: string) => void;
  onRetry: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  total: number;
  offset: number;
  onPageChange: (value: number) => void;
  onNew: () => void;
}) {
  const { t } = useTranslation("app");
  const entryCard = (entry: CustomerContact, selected: boolean) => (
    <SelectableCard
      key={entry.id}
      selected={selected}
      onSelect={() => onSelect(entry.id)}
    >
      <RadioDot selected={selected} />
      <div className="text-sm min-w-0">
        <p>
          {[
            entry.addressLine1,
            entry.addressLine2,
            entry.addressCity,
            entry.addressProvince,
            entry.addressPostalCode,
            entry.addressCountry,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <p className="mt-1 font-medium">{entry.contactName}</p>
        <p className="text-xs text-default-500">
          {[
            entry.label,
            entry.contactCompany,
            entry.contactPhone,
            entry.contactEmail,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {entry.pickupInstructions && (
          <p className="text-xs mt-1">{entry.pickupInstructions}</p>
        )}
      </div>
    </SelectableCard>
  );
  return (
    <SheetSection title={t("pickup.where.title")} hint={t("pickup.where.hint")}>
      {selectedEntry && entryCard(selectedEntry, true)}
      <Input
        className="mt-4"
        label={t("pickup.where.search")}
        value={search}
        onValueChange={onSearchChange}
      />
      {loading && <Spinner size="sm" />}
      {!!error && (
        <div>
          <p role="alert" className="text-sm text-danger">
            {t("pickup.where.loadError")}
          </p>
          <Button variant="bordered" isDisabled={loading} onPress={onRetry}>
            {t("actions.retry")}
          </Button>
        </div>
      )}
      {!loading && !error && contacts.length === 0 && (
        <p className="text-sm text-default-500 mt-2">
          {t("pickup.where.empty")}
        </p>
      )}
      <div className="space-y-2 mt-2">
        {contacts
          .filter((entry) => entry.id !== selectedEntry?.id)
          .map((entry) => entryCard(entry, entry.id === selectedEntryId))}
      </div>
      {(offset > 0 || offset + 10 < total) && (
        <div className="flex gap-2 mt-2">
          <Button
            variant="light"
            isDisabled={offset === 0}
            onPress={() => onPageChange(Math.max(0, offset - 10))}
          >
            {t("pickup.where.previous")}
          </Button>
          <Button
            variant="light"
            isDisabled={offset + 10 >= total}
            onPress={() => onPageChange(offset + 10)}
          >
            {t("pickup.where.next")}
          </Button>
        </div>
      )}
      <Button className="mt-3" variant="bordered" onPress={onNew}>
        {t("pickup.where.new")}
      </Button>
    </SheetSection>
  );
}

export function PickupScheduleWhatStep({
  groups,
  loading,
  selectedShipmentIds,
  setSelectedShipmentIds,
  notes,
  setNotes,
}: {
  groups: ShipmentGroup[];
  loading: boolean;
  selectedShipmentIds: Set<string>;
  setSelectedShipmentIds: (s: Set<string>) => void;
  notes: string;
  setNotes: (v: string) => void;
}) {
  const { t } = useTranslation("app");
  const toggle = (id: string) => {
    const next = new Set(selectedShipmentIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedShipmentIds(next);
  };

  const unavailable = groups
    .flatMap((group) => group.shipments)
    .filter((s) => s.pickupEligibility?.eligible === false);
  const bookableGroups = groups
    .map((group) => ({
      ...group,
      shipments: group.shipments.filter(
        (s) => s.pickupEligibility?.eligible !== false
      ),
    }))
    .filter((group) => group.shipments.length > 0);

  return (
    <>
      <SheetSection title={t("pickup.what.title")} hint={t("pickup.what.hint")}>
        {loading ? (
          <div className="text-sm text-default-500 flex items-center gap-2">
            <Spinner size="sm" />
            {t("pickup.common.loading")}
          </div>
        ) : bookableGroups.length === 0 ? (
          <div className="rounded-medium bg-default-50 py-6 text-center text-sm text-default-500">
            {t("pickup.what.empty")}
          </div>
        ) : (
          <div className="space-y-4">
            {bookableGroups.map((group) => {
              return (
                <div key={group.key}>
                  <div className="flex items-start gap-1.5 mb-2">
                    <MapPin className="h-3 w-3 text-default-400 mt-0.5 flex-shrink-0" />
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-default-500 leading-tight">
                      {formatAddressLine(
                        group.address,
                        t("pickup.common.addressMissing")
                      )}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() =>
                      setSelectedShipmentIds(
                        new Set([
                          ...selectedShipmentIds,
                          ...group.shipments.map((s) => s.id),
                        ])
                      )
                    }
                  >
                    {t("pickup.what.selectAll")}
                  </Button>
                  <div className="flex flex-col gap-2">
                    {group.shipments.map((s) => {
                      const recipientName =
                        s.tasks[0]?.contactName ??
                        t("pickup.common.unknownRecipient");
                      const totalWeightG = s.packages.reduce(
                        (sum, p) => sum + p.weightGrams,
                        0
                      );
                      const weightDisplay =
                        totalWeightG >= 1000
                          ? `${(totalWeightG / 1000).toFixed(1)} kg`
                          : `${totalWeightG} g`;
                      const checked = selectedShipmentIds.has(s.id);
                      return (
                        <SelectableCard
                          key={s.id}
                          selected={checked}
                          onSelect={() => toggle(s.id)}
                        >
                          <CheckBox selected={checked} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-mono text-[13px] font-semibold">
                                SHP-{s.shipmentNumber}
                              </span>
                              <span className="text-[11px] text-default-500 truncate">
                                → {recipientName}
                              </span>
                            </div>
                            <div className="text-[11.5px] text-default-500 mt-0.5">
                              {t("pickup.common.packageCount", {
                                count: s.packages.length,
                              })}{" "}
                              · {weightDisplay}
                            </div>
                          </div>
                        </SelectableCard>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <PickupUnavailableShipments
          shipments={unavailable}
          selectedIds={selectedShipmentIds}
        />
      </SheetSection>

      <SheetSection
        title={t("pickup.notes.title")}
        hint={t("pickup.notes.hint")}
        noDivider
      >
        <Textarea
          id="pickup-notes"
          value={notes}
          onValueChange={(v) => setNotes(v.slice(0, NOTES_MAX))}
          rows={3}
          placeholder={t("pickup.notes.placeholder")}
          variant="bordered"
        />
        <p className="text-[11px] text-default-400 text-right mt-1.5">
          {notes.length} / {NOTES_MAX}
        </p>
      </SheetSection>
    </>
  );
}

export function PickupScheduleWhenStep({
  pickupOptions,
  error,
  loading,
  date,
  setDate,
  pickupServiceId,
  setPickupServiceId,
  slotId,
  setSlotId,
  bookableSlots,
  dispatchTimeZone,
  onBack,
  onChangeLocation,
  onRetry,
}: {
  address: SenderAddress | null;
  pickupOptions: PickupOptions | undefined;
  error?: unknown;
  loading: boolean;
  date: string;
  setDate: (v: string) => void;
  pickupServiceId: string;
  setPickupServiceId: (v: string) => void;
  slotId: string;
  setSlotId: (v: string) => void;
  bookableSlots: PickupOptions["timeSlots"];
  dispatchTimeZone: string | null;
  onBack: () => void;
  onChangeLocation?: () => void;
  onRetry?: () => void;
}) {
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const services = pickupOptions?.pickupServices ?? [];
  const selectedService = services.find(
    (service) => service.id === pickupServiceId
  );
  const dateAvailability = selectedService?.dateAvailability ?? null;

  useEffect(() => {
    if (loading || !pickupOptions) return;
    if (slotId && !bookableSlots.some((slot) => slot.id === slotId)) {
      setSlotId("");
    }
  }, [loading, pickupOptions, bookableSlots, setSlotId, slotId]);

  // Date input bounds — driver tz aware.
  const minDate = dispatchTimeZone ? todayIsoInTz(dispatchTimeZone) : "";
  // Allow ~14 days ahead. Compute in tz to avoid drift around midnight UTC.
  const maxDate = useMemo(() => {
    if (!dispatchTimeZone || !minDate) return "";
    const [y, m, d] = minDate.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + 14);
    return dt.toISOString().slice(0, 10);
  }, [dispatchTimeZone, minDate]);

  const dateInput = (
    <input
      aria-label={t("pickup.when.date")}
      id="pickup-date"
      type="date"
      value={date}
      min={minDate}
      max={maxDate}
      disabled={!selectedService?.price}
      onChange={(e) => setDate(e.target.value)}
      className="w-full max-w-[260px] rounded-medium bg-content1 ring-1 ring-inset ring-default-200 px-3 py-2.5 text-sm focus:ring-primary focus:outline-none"
    />
  );

  if (isPickupFulfillmentSetupError(error)) {
    return (
      <SheetSection noDivider>
        <div
          role="alert"
          className="rounded-xl border border-default-200 bg-default-50 p-4"
        >
          <div className="flex items-start gap-3">
            <Info
              aria-hidden="true"
              className="h-5 w-5 flex-shrink-0 text-default-500 mt-0.5"
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-default-900">
                {pickupFulfillmentMessage(error, t)}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-default-600">
                {t("pickup.recovery.pickupUnavailableBody")}
              </p>
            </div>
          </div>
          <Button color="primary" fullWidth className="mt-4" onPress={onBack}>
            {t("pickup.recovery.reviewShipments")}
          </Button>
        </div>
      </SheetSection>
    );
  }

  if (error) {
    return (
      <SheetSection noDivider>
        <p role="alert" className="text-sm text-danger">
          {pickupFulfillmentMessage(error, t) ??
            (isPickupBookingError(error) && error.response?.data.message
              ? error.response.data.message
              : t("pickup.where.optionsError"))}
        </p>
        {isPickupFulfillmentTimingError(error) && (
          <div className="my-3">
            <label htmlFor="pickup-date" className="block text-sm mb-2">
              {t("pickup.when.date")}
            </label>
            {dateInput}
          </div>
        )}
        <Button variant="bordered" onPress={onBack}>
          {t("pickup.when.backToShipments")}
        </Button>
        {onRetry && (
          <Button variant="light" onPress={onRetry}>
            {t("pickup.recovery.retryOptions")}
          </Button>
        )}
      </SheetSection>
    );
  }

  if (loading && !pickupOptions) {
    return (
      <SheetSection title={t("pickup.steps.headlines.2")} noDivider>
        <div className="text-sm text-default-500 flex items-center gap-2">
          <Spinner size="sm" />
          {t("pickup.when.checking")}
        </div>
      </SheetSection>
    );
  }

  if (pickupOptions && !pickupOptions.serviceAreaCovered) {
    return (
      <SheetSection title={t("pickup.steps.headlines.2")} noDivider>
        <div className="rounded-medium border border-warning-200 bg-warning-50/40 p-3 text-sm text-warning-700">
          {t("pickup.when.notCovered")}
        </div>
        <Button variant="bordered" onPress={onChangeLocation ?? onBack}>
          {t("pickup.recovery.changeLocation")}
        </Button>
      </SheetSection>
    );
  }

  if (pickupOptions?.pickupServices.length === 0) {
    return (
      <SheetSection title={t("pickup.steps.headlines.2")} noDivider>
        <div className="space-y-3">
          <div className="rounded-medium border border-warning-200 bg-warning-50/40 p-3 text-sm text-warning-700">
            {t("pickup.when.noBundle")}
          </div>
          <Button
            variant="bordered"
            size="sm"
            onPress={onBack}
            startContent={<ArrowLeft className="h-4 w-4" />}
          >
            {t("pickup.when.backToShipments")}
          </Button>
        </div>
      </SheetSection>
    );
  }

  return (
    <>
      {services.length > 0 && (
        <SheetSection
          title={t("pickup.when.serviceTier")}
          hint={t("pickup.when.serviceHint")}
        >
          <div className="flex flex-col gap-2">
            {services.map((s) => {
              const isSelected = pickupServiceId === s.id;
              return (
                <SelectableCard
                  key={s.id}
                  selected={isSelected}
                  disabled={!s.price}
                  onSelect={() => setPickupServiceId(s.id)}
                >
                  <RadioDot selected={isSelected} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-default-900">
                      {s.name}
                    </div>
                    <p className="text-xs text-default-600">
                      {s.price
                        ? t("pickup.when.baselinePrice", {
                            amount: formatCurrency(
                              s.price.totalCents,
                              s.price.currency
                            ),
                          })
                        : t("pickup.when.priceUnavailable")}
                    </p>
                  </div>
                </SelectableCard>
              );
            })}
          </div>
          <p className="text-xs text-default-500 mt-2">
            {t("pickup.when.baselineHint")}
          </p>
        </SheetSection>
      )}

      <SheetSection title={t("pickup.when.date")}>
        {dateInput}
        {selectedService?.dispatch.city && (
          <p className="text-[11px] text-default-500 mt-2">
            {t("pickup.when.timeInCity", {
              city: selectedService.dispatch.city,
            })}
          </p>
        )}
      </SheetSection>

      <SheetSection
        title={t("pickup.when.timeWindow")}
        hint={t("pickup.when.timeWindowHint")}
        noDivider
      >
        {!selectedService ? (
          <p className="text-sm text-default-500" role="status">
            {t("pickup.when.chooseService")}
          </p>
        ) : bookableSlots.length === 0 ? (
          dateAvailability?.status === "CLOSED" && dispatchTimeZone ? (
            <p className="text-sm text-default-500" role="status">
              {dateAvailability.nextAvailableDate
                ? t("pickup.closed", {
                    day: formatDateInTz(date, dispatchTimeZone, "EEEE", locale),
                    date: formatDateInTz(
                      dateAvailability.nextAvailableDate,
                      dispatchTimeZone,
                      "EEE, MMM d",
                      locale
                    ),
                  })
                : t("pickup.closedNoNext", {
                    day: formatDateInTz(date, dispatchTimeZone, "EEEE", locale),
                  })}
            </p>
          ) : (
            <p className="text-sm text-default-500">
              {t("pickup.when.noSlots")}
            </p>
          )
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {bookableSlots.map((s) => {
              const isSelected = slotId === s.id;
              const range = dispatchTimeZone
                ? formatSlotRangeInTz(s, dispatchTimeZone, date, locale)
                : `${s.startTime} – ${s.endTime}`;
              // remaining === 0 → full (disable). null/undefined → uncapped.
              const isFull = s.remaining === 0;
              return (
                <SelectableCard
                  key={s.id}
                  selected={isSelected}
                  disabled={isFull}
                  onSelect={() => setSlotId(s.id)}
                  className="flex-col items-start"
                >
                  <div className="font-mono text-[13.5px] font-semibold text-default-900">
                    {range}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className="text-[11px] text-default-500">
                      {s.name}
                    </span>
                    <SlotCapacityLabel remaining={s.remaining} />
                  </div>
                </SelectableCard>
              );
            })}
          </div>
        )}
      </SheetSection>
    </>
  );
}

function StepConfirm({
  address,
  date,
  slot,
  dispatchTimeZone,
  preview,
  paymentMethods,
  paymentChoice,
  setPaymentChoice,
  paymentMethodId,
  setPaymentMethodId,
  creditBalanceCents,
  creditFrozen,
  canUseCredit,
  canUseNetTerms,
  netTermsLabel,
  netTermsUnavailableReason,
  submitError,
}: {
  address: SenderAddress | null;
  date: string;
  slot: PickupOptions["timeSlots"][number] | null;
  dispatchTimeZone: string | null;
  preview: PickupPreview | null;
  paymentMethods: {
    id: string;
    last4?: string | null;
    brand?: string | null;
    isDefault?: boolean;
  }[];
  paymentChoice: PickupPaymentChoice | "";
  setPaymentChoice: (v: PickupPaymentChoice) => void;
  paymentMethodId: string;
  setPaymentMethodId: (v: string) => void;
  creditBalanceCents: number;
  creditFrozen: boolean;
  canUseCredit: boolean;
  canUseNetTerms: boolean;
  netTermsLabel: string | null;
  netTermsUnavailableReason: string | null;
  submitError: string | null;
}) {
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const dateLabel = dispatchTimeZone
    ? formatDateInTz(date, dispatchTimeZone, "EEEE, MMMM d", locale)
    : date;
  const slotLabel = slot
    ? dispatchTimeZone
      ? `${slot.name} · ${formatSlotRangeInTz(
          slot,
          dispatchTimeZone,
          date,
          locale
        )}`
      : slot.name
    : "";

  const summaryRows: { k: string; v: string }[] = [
    {
      k: t("pickup.confirm.address"),
      v: formatAddressLine(address, t("pickup.common.addressMissing")),
    },
    {
      k: t("pickup.confirm.when"),
      v: `${dateLabel}${slotLabel ? ` · ${slotLabel}` : ""}`,
    },
    ...(preview
      ? [
          {
            k: t("pickup.confirm.shipments"),
            v: t("pickup.common.shipmentCount", {
              count: preview.shipments.length,
            }),
          },
        ]
      : []),
  ];

  const grandTotalCents = preview
    ? preview.totalCents + preview.taxCents
    : null;
  const currency = preview?.currency ?? "CAD";

  return (
    <>
      <SheetSection title={t("pickup.confirm.summary")}>
        <div className="rounded-medium bg-default-50 p-4">
          {summaryRows.map((r, i) => (
            <div
              key={r.k}
              className={`flex justify-between gap-2.5 py-2 ${
                i === summaryRows.length - 1
                  ? ""
                  : "border-b border-dashed border-default-200"
              }`}
            >
              <span className="text-[11px] font-semibold uppercase tracking-wider text-default-500 flex-shrink-0">
                {r.k}
              </span>
              <span className="text-[13px] text-default-900 text-right max-w-[280px]">
                {r.v}
              </span>
            </div>
          ))}
        </div>
      </SheetSection>

      {preview && (
        <SheetSection title={t("pickup.confirm.charges")}>
          <div className="rounded-medium bg-content1 ring-1 ring-inset ring-default-200 p-4 space-y-2 text-sm">
            {preview.shipments.map((s) => (
              <div key={s.id} className="flex justify-between">
                <span className="text-default-600">
                  {t("pickup.confirm.pickupLine", {
                    shipmentNumber: s.shipmentNumber,
                  })}
                </span>
                <span className="font-mono">
                  {formatCurrency(s.pickupPriceCents, preview.currency)}
                </span>
              </div>
            ))}
            {preview.bundleDiscountCents > 0 && (
              <div className="flex justify-between text-success-700 pt-2 border-t border-divider">
                <span>
                  {t("pickup.confirm.bundleDiscountWithCount", {
                    count: preview.shipments.length,
                  })}
                </span>
                <span className="font-mono">
                  −
                  {formatCurrency(
                    preview.bundleDiscountCents,
                    preview.currency
                  )}
                </span>
              </div>
            )}
            {preview.taxCents > 0 && (
              <div className="flex justify-between text-default-600 pt-2 border-t border-divider">
                <span>{t("pickup.common.hst")}</span>
                <span className="font-mono">
                  {formatCurrency(preview.taxCents, preview.currency)}
                </span>
              </div>
            )}
          </div>
        </SheetSection>
      )}

      <SheetSection title={t("pickup.confirm.payWith")}>
        <div className="flex flex-col gap-2">
          {paymentMethods.length > 0 && (
            <div className="space-y-2">
              <SelectableCard
                selected={paymentChoice === "SAVED_CARD"}
                onSelect={() => setPaymentChoice("SAVED_CARD")}
              >
                <RadioDot selected={paymentChoice === "SAVED_CARD"} />
                <CreditCard className="h-4 w-4 text-default-500 mt-0.5" />
                <span className="text-sm font-semibold">
                  {t("pickup.common.card")}
                </span>
              </SelectableCard>
              <Select
                aria-label={t("pickup.confirm.savedCard")}
                placeholder={t("pickup.confirm.savedCard")}
                selectedKeys={
                  paymentMethods.some((method) => method.id === paymentMethodId)
                    ? [paymentMethodId]
                    : []
                }
                onSelectionChange={(keys) => {
                  if (keys === "all") return;
                  const id = Array.from(keys)[0];
                  if (typeof id !== "string") return;
                  setPaymentMethodId(id);
                  setPaymentChoice("SAVED_CARD");
                }}
              >
                {paymentMethods.map((pm, index) => {
                  const duplicate = paymentMethods.some(
                    (other) =>
                      other.id !== pm.id &&
                      other.brand === pm.brand &&
                      other.last4 === pm.last4
                  );
                  const label = [
                    t("pickup.common.cardEnding", {
                      brand: pm.brand ?? t("pickup.common.card"),
                      last4: pm.last4 ?? "????",
                    }),
                    pm.isDefault ? t("pickup.common.default") : null,
                    duplicate
                      ? t("pickup.confirm.savedCardNumber", {
                          number: index + 1,
                        })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <SelectItem key={pm.id} textValue={label}>
                      {label}
                    </SelectItem>
                  );
                })}
              </Select>
            </div>
          )}

          <SelectableCard
            selected={paymentChoice === "CREDIT"}
            disabled={!canUseCredit}
            onSelect={() => setPaymentChoice("CREDIT")}
          >
            <RadioDot selected={paymentChoice === "CREDIT"} />
            <Wallet className="h-4 w-4 text-success-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-default-900">
                {t("pickup.confirm.accountCredit")}
              </div>
              <div className="text-[11.5px] text-default-500 mt-0.5">
                {creditFrozen
                  ? t("pickup.confirm.creditFrozen")
                  : t("pickup.confirm.creditAvailable", {
                      amount: formatCurrency(creditBalanceCents, currency),
                    })}
              </div>
            </div>
          </SelectableCard>

          <SelectableCard
            selected={paymentChoice === "INVOICE"}
            disabled={!canUseNetTerms}
            onSelect={() => setPaymentChoice("INVOICE")}
          >
            <RadioDot selected={paymentChoice === "INVOICE"} />
            <FileText className="h-4 w-4 text-primary mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-default-900">
                {t("pickup.confirm.netTerms")}
              </div>
              <div className="text-[11.5px] text-default-500 mt-0.5">
                {canUseNetTerms
                  ? t("pickup.confirm.netTermsSource", {
                      terms: netTermsLabel ?? t("pickup.confirm.netTerms"),
                    })
                  : (netTermsUnavailableReason ??
                    t("pickup.confirm.netTermsUnavailable"))}
              </div>
            </div>
          </SelectableCard>

          {paymentMethods.length === 0 && !canUseCredit && !canUseNetTerms && (
            <p className="text-sm text-warning-700">
              {t("pickup.confirm.noPaymentMethod")}
            </p>
          )}
        </div>
      </SheetSection>

      {grandTotalCents != null && (
        <SheetSection noDivider>
          <div className="rounded-medium bg-primary-50 px-4 py-3.5 flex items-center justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-default-500">
                {t("pickup.confirm.totalToday")}
              </div>
              <div className="font-mono text-2xl font-bold text-default-900 mt-1">
                {formatCurrency(grandTotalCents, currency)}
              </div>
            </div>
            <div className="text-right text-[11px] text-default-600">
              <div>
                {t("pickup.common.shipmentCount", {
                  count: preview?.shipments.length ?? 0,
                })}
              </div>
              <div>
                {paymentChoice === "INVOICE"
                  ? t("pickup.confirm.addedToInvoice")
                  : paymentChoice === "CREDIT"
                    ? t("pickup.confirm.deductedFromCredit")
                    : t("pickup.confirm.chargedAtCompletion")}
              </div>
            </div>
          </div>
        </SheetSection>
      )}

      {submitError && (
        <SheetSection noDivider>
          <div className="rounded-medium border border-danger-200 bg-danger-50/40 p-3 text-sm text-danger-700">
            {submitError}
          </div>
        </SheetSection>
      )}
    </>
  );
}
